import { useEffect, useState } from "react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { Locale } from "@/lib/i18n/config";
import type { CouponView } from "@/lib/api/types";
import { copyTextToClipboard } from "@/lib/browser/clipboard";
import { SpotlightCard } from "@/shared/components/SpotlightCard";
import {
  couponMinOrderText,
  couponStatusTone,
  couponTypeBadge,
  couponUsage,
  couponValidityText,
  couponValueText,
  formatCouponExpiry,
} from "@/modules/coupons/lib/coupon-display";

type CouponCardProps = {
  locale: Locale;
  coupon: CouponView;
  isMutating: boolean;
  onEdit: (coupon: CouponView) => void;
  onShare: (coupon: CouponView) => void;
  onDelete: (coupon: CouponView) => void;
  /** Extra classes on the card root (e.g. grid col-span for the odd last card). */
  className?: string;
};

/**
 * Ticket-style coupon card matching the design: type chip + status, big value,
 * perforated notch, code + copy, redeemed progress, min-order + expiry, and a
 * ⋮ menu (edit / share / delete).
 */
export function CouponCard({
  locale,
  coupon,
  isMutating,
  onEdit,
  onShare,
  onDelete,
  className,
}: CouponCardProps) {
  const t = useTranslations();
  const [copied, setCopied] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const tone = couponStatusTone(coupon, t);
  const typeBadge = couponTypeBadge(coupon, t);
  const value = couponValueText(coupon, locale, t);
  const { maxLabel, pct, barColor } = couponUsage(coupon, tone);
  const minOrderText = couponMinOrderText(coupon, locale, t);
  const validityText = couponValidityText(coupon, locale, t);
  const expiryText = coupon.expiresAt
    ? formatCouponExpiry(coupon.expiresAt, locale)
    : null;

  const copyCode = async () => {
    if (await copyTextToClipboard(coupon.code)) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <SpotlightCard
      glow={`color-mix(in oklab, ${typeBadge.fg} 10%, transparent)`}
      className={[
        "flex flex-col rounded-[18px] border border-[color:var(--card-br)] bg-[color:var(--surface)] shadow-[0_1px_2px_rgba(0,0,0,0.04)]",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      suppressTooltip={menuOpen}
      tooltip={
        <>
          <div className="mb-2 whitespace-nowrap font-mono text-[12.5px] font-bold tracking-[0.04em]">
            {coupon.code}
          </div>
          <div className="flex items-center justify-between gap-5 text-[11.5px]">
            <span className="text-[color:var(--text-muted)]">
              {t(keys.coupons.tableDiscount)}
            </span>
            <span className="font-bold" style={{ color: typeBadge.fg }}>
              <bdi>{value}</bdi>
            </span>
          </div>
          <div className="mt-1.5 flex items-center justify-between gap-5 text-[11.5px]">
            <span className="text-[color:var(--text-muted)]">
              {t(keys.coupons.redeemedLabel)}
            </span>
            <span className="font-semibold text-[color:var(--text-primary)]">
              <bdi dir="ltr">
                {coupon.usesCount} / {maxLabel}
              </bdi>
            </span>
          </div>
          {minOrderText ? (
            <div className="mt-1.5 flex items-center justify-between gap-5 text-[11.5px]">
              <span className="text-[color:var(--text-muted)]">
                {t(keys.coupons.minOrderLabel)}
              </span>
              <span className="font-semibold text-[color:var(--text-primary)]">
                <bdi>{minOrderText}</bdi>
              </span>
            </div>
          ) : null}
          <div className="mt-1.5 flex items-center justify-between gap-5 text-[11.5px]">
            <span className="text-[color:var(--text-muted)]">
              {t(keys.coupons.expiryLabel)}
            </span>
            <span className="font-semibold text-[color:var(--text-primary)]">
              {expiryText ?? t(keys.coupons.noExpiry)}
            </span>
          </div>
        </>
      }
    >
      {/* Top */}
      <div className="px-[17px] pb-[14px] pt-[15px]">
        <div className="mb-[13px] flex items-center justify-between gap-2">
          <span
            className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold"
            style={{ background: typeBadge.bg, color: typeBadge.fg }}
          >
            {typeBadge.label}
          </span>
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-[9px] py-1 text-[11px] font-semibold"
            style={{ background: tone.bg, color: tone.fg }}
          >
            <span
              aria-hidden="true"
              className="h-[5px] w-[5px] rounded-full"
              style={{ background: tone.dot }}
            />
            {tone.label}
          </span>
        </div>
        <div className="text-[27px] font-extrabold leading-[1.05] tracking-[-0.02em] text-[color:var(--text-primary)]">
          <bdi>{value}</bdi>
        </div>
        <div className="mt-[7px] text-[12px] text-[color:var(--text-muted)]">
          <bdi>{minOrderText ?? t(keys.coupons.noMinimum)}</bdi>
        </div>
      </div>

      {/* Perforated notch */}
      <div className="relative h-0">
        <div className="absolute -start-[7px] -top-[7px] h-3.5 w-3.5 rounded-full bg-[color:var(--canvas)]" />
        <div className="absolute -end-[7px] -top-[7px] h-3.5 w-3.5 rounded-full bg-[color:var(--canvas)]" />
        <div
          className="absolute -top-px start-[9px] end-[9px] h-0.5"
          style={{
            backgroundImage:
              "repeating-linear-gradient(90deg,var(--border-strong) 0 2px,transparent 2px 8px)",
          }}
        />
      </div>

      {/* Bottom */}
      <div className="rounded-b-[18px] bg-[color:var(--background-muted)] px-[17px] pb-4 pt-[15px]">
        <div className="flex items-center gap-2.5">
          <div className="flex min-w-0 flex-1 items-center gap-2 rounded-[10px] border border-dashed border-[color:var(--border-strong)] bg-[color:var(--surface)] px-3 py-2.5">
            <TicketIcon />
            <span className="truncate font-mono text-[13.5px] font-bold tracking-[0.06em] text-[color:var(--text-primary)]">
              {coupon.code}
            </span>
          </div>
          <button
            type="button"
            onClick={() => void copyCode()}
            aria-label={t(keys.coupons.copyCode)}
            title={copied ? t(keys.common.copied) : t(keys.coupons.copyCode)}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] border border-[color:var(--card-br)] bg-[color:var(--surface)] text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--hover)] lg:h-9 lg:w-9"
          >
            {copied ? <CheckIcon /> : <CopyIcon />}
          </button>
        </div>

        {/* Redeemed */}
        <div className="mt-3.5">
          <div className="mb-1.5 flex items-center justify-between text-[11.5px] font-semibold text-[color:var(--text-muted)]">
            <span>{t(keys.coupons.redeemedLabel)}</span>
            <span className="text-[color:var(--text-secondary)]">
              <bdi dir="ltr">
                {coupon.usesCount} / {maxLabel}
              </bdi>
            </span>
          </div>
          {coupon.maxUses !== null ? (
            <div className="h-1.5 overflow-hidden rounded-full bg-[color:var(--track)]">
              <div
                className="h-full rounded-full"
                style={{ width: `${pct}%`, background: barColor }}
              />
            </div>
          ) : null}
        </div>

        {/* Validity + menu */}
        <div className="mt-3.5 flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[color:var(--text-muted)]">
            <ClockIcon />
            {validityText}
          </span>
          <Menu as="div" className="relative">
            {({ open }) => (
              <>
                <MenuOpenSync open={open} onChange={setMenuOpen} />
            <MenuButton
              aria-label={t(keys.common.actions)}
              disabled={isMutating}
              className="flex h-10 w-10 items-center justify-center rounded-[9px] border border-[color:var(--card-br)] bg-[color:var(--surface)] text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--hover)] disabled:opacity-50 lg:h-8 lg:w-8"
            >
              <DotsIcon />
            </MenuButton>
            <MenuItems
              anchor="bottom end"
              className="z-50 mt-1.5 w-[160px] overflow-hidden rounded-[12px] border border-[color:var(--border-light)] bg-[color:var(--surface)] p-1.5 shadow-[0_12px_32px_rgba(15,20,40,0.14)] focus:outline-none"
            >
              <MenuItem>
                <button
                  type="button"
                  onClick={() => onEdit(coupon)}
                  className="flex w-full items-center gap-2.5 rounded-[8px] px-2.5 py-2 text-start text-[13px] font-medium text-[color:var(--text-primary)] data-[focus]:bg-[color:var(--surface-alt)]"
                >
                  <EditIcon />
                  {t(keys.common.edit)}
                </button>
              </MenuItem>
              <MenuItem>
                <button
                  type="button"
                  onClick={() => onShare(coupon)}
                  className="flex w-full items-center gap-2.5 rounded-[8px] px-2.5 py-2 text-start text-[13px] font-medium text-[color:var(--text-primary)] data-[focus]:bg-[color:var(--surface-alt)]"
                >
                  <ShareIcon />
                  {t(keys.coupons.shareTitle)}
                </button>
              </MenuItem>
              <div
                aria-hidden="true"
                className="my-1 h-px bg-[color:var(--border-light)]"
              />
              <MenuItem>
                <button
                  type="button"
                  onClick={() => onDelete(coupon)}
                  className="flex w-full items-center gap-2.5 rounded-[8px] px-2.5 py-2 text-start text-[13px] font-semibold text-[color:var(--error-500)] data-[focus]:bg-[color:var(--error-500)]/10"
                >
                  <TrashIcon />
                  {t(keys.common.delete)}
                </button>
              </MenuItem>
            </MenuItems>
              </>
            )}
          </Menu>
        </div>
      </div>
    </SpotlightCard>
  );
}

/**
 * Bridges the Headless UI Menu's render-prop `open` state up to the card so it
 * can suppress the SpotlightCard tooltip while the dropdown is open. Uses an
 * effect (not render) to avoid setState-during-render.
 */
function MenuOpenSync({
  open,
  onChange,
}: {
  open: boolean;
  onChange: (open: boolean) => void;
}) {
  useEffect(() => {
    onChange(open);
  }, [open, onChange]);
  return null;
}

/* ── Icons ─────────────────────────────────────────────────────────── */

const S = {
  fill: "none",
  stroke: "currentColor" as const,
  strokeWidth: 1.7,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

function TicketIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" {...S} className="shrink-0 text-[color:var(--text-disabled)]">
      <path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0l-7.2-7.2A2 2 0 0 1 3 12V4a1 1 0 0 1 1-1h8a2 2 0 0 1 1.4.6l7.2 7.2a2 2 0 0 1 0 2.6Z" />
      <circle cx="7.5" cy="7.5" r=".8" />
    </svg>
  );
}
function CopyIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" {...S}>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V5a2 2 0 0 1 2-2h10" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" {...S} strokeWidth={2.2} className="text-[color:var(--success-500)]">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}
function ClockIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" {...S}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}
function DotsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="5" r="1.5" />
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="12" cy="19" r="1.5" />
    </svg>
  );
}
function EditIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" {...S}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}
function ShareIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" {...S}>
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <path d="m8.6 13.5 6.8 4M15.4 6.5l-6.8 4" />
    </svg>
  );
}
function TrashIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" {...S}>
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    </svg>
  );
}
